import React, { useEffect, useState } from 'react';
import {
    TextField,
    Button,
    Link,
    Stack,
    Box,
    Typography,
} from '@mui/material';
import { useAuthContext } from '../../context/auth/AuthContext';
import { useLoadingContext } from '../../context/loading/LoadingContext';
import ForgotPasswordModal from './modals/ForgotPasswordModal';
import Logo from '../../resources/images/logo/bizismart.png';
import LoadingNavBar from '../../components/navbar/LoadingNavBar';
import { StringKeys } from '../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserContext } from '../../context/user_context/UserContext';
import { UserStatus } from '../../components/routes/routing_statuses/RoutingStatuses';

const LoginPage = () => {
    const [emailAddress, setEmailAddress] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const { loginUser } = useAuthContext();
    const { setLoadingData } = useLoadingContext();
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { firebaseUser } = useUserContext();
    const { user } = useAuthContext();

    useEffect(() => {
        if(firebaseUser && user != UserStatus.UNAUTHENTICATED) {
            const params = new URLSearchParams(location.search);
            const returnUrl = params.get('returnUrl');
            navigate(returnUrl || '/home');
        }
    },[firebaseUser, location.search, navigate])

    const handleLogin = async () => {
        setLoadingData(true);
        setError("");
        const response = await loginUser(emailAddress, password);
        if (response.error) {
            if (response.error === 'Firebase: Error (auth/invalid-email).') {
                setError(t(StringKeys.INVALID_EMAIL_PLEASE_TRY_AGAIN));
            } else if (response.error === 'Firebase: Error (auth/invalid-credential).') {
                setError(t(StringKeys.INVALID_PASSWORD_PLEASE_TRY_AGAIN));
            } else {
                setError(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN));
            }
        }
        setLoadingData(false);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleLogin();
        }
    };

    return (
        <>
        <LoadingNavBar showLoginButton={false} />
        <Box
            className="hero-section"
            sx={{
                height: '100%',
                background: 'linear-gradient(135deg, rgba(57, 172, 231, 0.95), rgba(16, 42, 67, 0.95))',
                animation: 'gradientAnimation 15s ease infinite',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '@keyframes gradientAnimation': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                },
            }}
        >
            <Box
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    borderRadius: 3,
                    textAlign: 'center',
                    width: '95%',
                    maxWidth: '440px',
                    margin: '0 auto',
                    my: 5,
                    p: { xs: 3, sm: 4 },
                    WebkitTransform: 'translateZ(0)',
                }}
            >
                <img src={Logo} alt="BiziSmart Logo" style={{ height: '60px', marginBottom: '20px' }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#102a43', mb: 3 }}>
                    {t(StringKeys.LOG_IN)}
                </Typography>
                
                <Stack spacing={3}>
                    <TextField
                        autoFocus
                        id="email"
                        label={t(StringKeys.EMAIL)}
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                        error={!!error}
                        helperText={error}
                        onKeyDown={handleKeyDown}
                        sx={{
                            '& .MuiInputBase-root': {
                                height: '56px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '12px'
                            },
                            '& .MuiInputBase-input': {
                                fontSize: '16px',
                                '-webkit-text-size-adjust': 'none'
                            },
                            '& .MuiInputLabel-root': {
                                fontSize: '16px'
                            }
                        }}
                    />
                    <TextField
                        id="password"
                        label={t(StringKeys.PASSWORD)}
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={handleKeyDown}
                        sx={{
                            '& .MuiInputBase-root': {
                                height: '56px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '12px'
                            },
                            '& .MuiInputBase-input': {
                                fontSize: '16px',
                                '-webkit-text-size-adjust': 'none'
                            },
                            '& .MuiInputLabel-root': {
                                fontSize: '16px'
                            }
                        }}
                    />
                    
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            textAlign: 'center',
                            fontSize: '13px',
                            lineHeight: 1.5,
                            color: '#64748b',
                            px: 1,
                            mt: 1
                        }}
                    >
                        {t(StringKeys.BY_CLICKING_LOG_IN_YOU_AGREE_TO_OUR_)}{' '}
                        <Link
                            onClick={() => navigate('/terms-of-service')}
                            underline="hover"
                            sx={{ 
                                cursor: 'pointer', 
                                color: '#39ace7', 
                                fontWeight: '600',
                                display: 'inline-block',
                                py: 0.5,
                                px: 0.25
                            }}
                        >
                            {t(StringKeys.TERMS_OF_SERVICE)}
                        </Link>{' '}
                        {t(StringKeys.AND)}{' '}
                        <Link
                            onClick={() => navigate('/privacy-policy')}
                            underline="hover"
                            sx={{ 
                                cursor: 'pointer', 
                                color: '#39ace7', 
                                fontWeight: '600',
                                display: 'inline-block',
                                py: 0.5,
                                px: 0.25
                            }}
                        >
                            {t(StringKeys.PRIVACY_POLICY)}
                        </Link>
                    </Typography>
                    
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleLogin}
                        sx={{ 
                            textTransform: 'none', 
                            fontWeight: '600',
                            height: '56px',
                            fontSize: '16px',
                            borderRadius: '30px',
                            boxShadow: '0 4px 12px rgba(57, 172, 231, 0.2)',
                            mt: 2,
                            '&:hover': {
                                boxShadow: '0 6px 16px rgba(57, 172, 231, 0.3)',
                            },
                            '&:active': {
                                transform: 'scale(0.98)',
                            },
                        }}
                    >
                        {t(StringKeys.LOG_IN)}
                    </Button>
                    
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => setShowForgotPasswordModal(true)}
                            sx={{ 
                                textAlign: 'center', 
                                color: '#39ace7', 
                                cursor: 'pointer',
                                fontSize: '15px',
                                fontWeight: '500',
                                textDecoration: 'none',
                                py: 0.5,
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            {t(StringKeys.FORGOT_PASSWORD)}
                        </Link>
                        {/* <Typography 
                            variant="body2" 
                            sx={{ 
                                textAlign: 'center',
                                color: '#64748b',
                                fontSize: '15px'
                            }}
                        >
                            {t(StringKeys.DONT_HAVE_AN_ACCOUNT)}{' '}
                            <Link
                                component="button"
                                onClick={() => navigate('/signup')}
                                sx={{ 
                                    cursor: 'pointer', 
                                    color: '#39ace7', 
                                    fontWeight: '600',
                                    textDecoration: 'none',
                                    py: 0.5,
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                {t(StringKeys.SIGN_UP)}
                            </Link>
                        </Typography> */}
                    </Stack>
                </Stack>
            </Box>
            {showForgotPasswordModal && (
                <ForgotPasswordModal
                    show={showForgotPasswordModal}
                    onHide={() => setShowForgotPasswordModal(false)}
                />
            )}
        </Box>
        </>
    );
};

export default LoginPage;